<template>
  <ion-card
    v-if="invoiceSelected"
    class="border-default no-shadow rental-card ion-no-margin"
    color="light"
  >
    <div class="ion-padding bg-white b-grey">
      <div
        v-for="paymentItem in invoiceSelected.Invoice.InvoiceItems"
        :key="`payment-${paymentItem.id}`"
        class="d-flex align-center justify-space-between w-100 price-subtitle"
      >
        <p class="font-sm grey bold-normal hide">
          {{ paymentItem.RelatedPaymentItem.title }}
        </p>
        <p class="font-sm">
          {{
            amountMask(
              Number(paymentItem.RelatedPaymentItem.amount),
              paymentItem.RelatedPaymentItem.currency,
            )
          }}
        </p>
      </div>
    </div>

    <div class="ion-padding bg-white b-grey">
      <div class="d-flex align-center justify-space-between w-100 price-subtitle">
        <p class="font-sm bold-medium grey">{{ t('invoices.subtotal') }}</p>
        <p class="font-sm">{{ amountMask(getSubtotal, getCurrency) }}</p>
      </div>

      <div v-if="getPaymentTaxes.length > 0">
        <div
          v-for="(tax, idx) in getPaymentTaxes"
          :key="`tax-${idx}`"
          class="d-flex align-center justify-space-between w-100 price-subtitle"
        >
          <p class="font-sm grey hide">{{ tax.name }}</p>
          <p class="font-sm">{{ amountMask(Number(tax.amount), getCurrency) }}</p>
        </div>
      </div>
    </div>

    <div class="ion-padding bg-white b-grey">
      <div class="d-flex align-center justify-space-between w-100 price-subtitle">
        <p class="font-sm bold-medium grey">{{ t('invoices.total') }}</p>
        <p class="font-lg bold-medium">{{ amountMask(getTotal, getCurrency) }}</p>
      </div>
    </div>
  </ion-card>
</template>

<script setup lang="ts">
  import { useBillingStore } from '@/stores/billing-store';
  import { amountMask } from '@/utils/validations/formats';
  import { IonCard } from '@ionic/vue';
  import { storeToRefs } from 'pinia';
  import { computed } from 'vue';
  import { useI18n } from 'vue-i18n';

  const { t } = useI18n();
  const { invoiceSelected } = storeToRefs(useBillingStore());

  interface TaxForm {
    name: string;
    amount: number;
  }

  interface Taxes {
    [key: string]: TaxForm;
  }

  const getCurrency = computed(() => {
    return invoiceSelected.value
      ? invoiceSelected.value.Invoice.InvoiceItems[0].RelatedPaymentItem.currency
      : 'EUR';
  });

  const getSubtotal = computed(() => {
    if (!invoiceSelected.value) {
      return 0;
    }
    const additionalList = invoiceSelected.value.Invoice.InvoiceItems.reduce(
      (sum, obj) => sum + obj.netAmount,
      0,
    );

    const total = additionalList;

    return total;
  });

  const getTotal = computed(() => {
    const taxAmount =
      getPaymentTaxes.value.length > 0
        ? getPaymentTaxes.value.reduce((sum, obj) => sum + Number(obj.amount), 0)
        : 0;

    const total = Number(taxAmount) + getSubtotal.value;

    return total;
  });

  const getPaymentTaxes = computed(() => {
    const taxes: Taxes = {};

    if (!invoiceSelected.value) {
      return [];
    }

    invoiceSelected.value.Invoice.InvoiceItems.forEach((item) => {
      const netTotal = item.RelatedPaymentItem.amount;
      item.PaymentItem.taxes.forEach((tax) => {
        const { name, value } = tax;

        if (!taxes[name]) {
          taxes[name] = {
            name: name,
            amount: 0,
          };
        }
        const calculatedAmount = (value / 100) * netTotal;

        taxes[name].amount += calculatedAmount;
      });
    });

    const result = Object.values(taxes).map((tax) => ({
      name: tax.name,
      amount: tax.amount.toFixed(2),
    }));

    return result;
  });
</script>

<style lang="css" scoped>
  .hide {
    width: 220px;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
  }
</style>
